import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [business, setBusiness] = useState(null);
    const [viewingBusinessId, setViewingBusinessId] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadUser = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const { data } = await authApi.getMe();
            setUser(data.user);
            setBusiness(data.business || null);
            setViewingBusinessId(data.viewing_business_id || null);
        } catch {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('business');
            setUser(null);
            setBusiness(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadUser(); }, [loadUser]);

    const login = async (email, password) => {
        try {
            const { data } = await authApi.login({ email, password });
            localStorage.setItem('token', data.access_token);
            setUser(data.user);
            setBusiness(data.business || null);
            return { success: true, user: data.user };
        } catch (err) {
            return { success: false, error: err.response?.data?.detail || 'Login failed' };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('business');
        setUser(null);
        setBusiness(null);
        setViewingBusinessId(null);
    };

    const updateToken = (newToken, newBusiness = null) => {
        localStorage.setItem('token', newToken);
        if (newBusiness) setBusiness(newBusiness);
        loadUser();
    };

    const isAdmin = user?.role === 'admin';
    const isManager = user?.role === 'manager';
    const isManagerOrAdmin = isAdmin || isManager;
    const hasBusiness = !!business || !!viewingBusinessId;
    const enabledFeatures = business?.enabled_features || [];

    const isFeatureEnabled = (featureKey) => {
        // Admins see everything; no business = no filtering
        if (isAdmin) return true;
        if (!business) return true;
        // If business has no enabled_features field yet, show all
        if (!business.enabled_features) return true;
        return enabledFeatures.includes(featureKey);
    };

    return (
        <AuthContext.Provider value={{
            user, business, viewingBusinessId, loading,
            login, logout, updateToken, loadUser,
            isAdmin, isManager, isManagerOrAdmin, hasBusiness,
            isAuthenticated: !!user,
            enabledFeatures, isFeatureEnabled,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};
